import React, { useEffect, useState } from "react";
import CustomCard from "../../../../../components/CustomCard";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { TOTAL_MEALS_COUNT_GRID_MODEL } from "../Models/Model";
import CustomTooltip from "../../../../../components/CustomTooltip";

/**
 * @Author: Karen Yunuen Pozos Arias
 * @JobTitle: Full Stack Developer - IBL
 * @Email: kpozos@ibl.mx
 *
 * @component TotalMealsCountGridView
 * @description
 * Renders a responsive grid of cards showing KPIs for total meals.
 * - Pulls KPI data from Redux state (`FoodieOrdersSlice.getTotalMealsKpisGrid`).
 * - Dynamically maps values to each card based on `TOTAL_MEALS_COUNT_GRID_MODEL`.
 * - Displays an icon, title, current name, and total count per card.
 * - Uses `CustomTooltip` to handle text overflow gracefully.
 *
 * @returns {JSX.Element} Grid of total meals KPI cards.
 */
const TotalMealsCountGridView = () => {
    const { t } = useTranslation();

    // Get KPI data from Redux
    const { getTotalMealsKpisGrid } = useSelector((state) => state?.FoodieOrdersSlice);

    // Local state for items displayed in the grid, initialized with the model
    const [items, setItems] = useState({ ...TOTAL_MEALS_COUNT_GRID_MODEL });

    useEffect(() => {
        if (getTotalMealsKpisGrid !== null && Object.keys(getTotalMealsKpisGrid)?.length > 0) {
            let aux = { ...items };

            // Iterate over each entry from Redux and map it to the corresponding card
            Object.entries(getTotalMealsKpisGrid)?.map(([dbKey, dbValue]) => {
                Object.entries(aux)?.map(([propName, propValue]) => {
                    // Update card name if the key matches
                    if (propValue.keyName === dbKey)
                        aux = { ...aux, [propName]: { ...aux[propName], name: dbValue } };

                    // Update card total if the key matches
                    if (propValue.keyCount === dbKey)
                        aux = { ...aux, [propName]: { ...aux[propName], total: dbValue } };
                });
            });

            setItems(aux);
        }
    }, [getTotalMealsKpisGrid]);

    return (
        <div className={"row row-cols-sm-1 row-cols-md-2 row-cols-lg-5 mt-3"}>
            {
                Object.entries(items).map(([key, item]) => {
                    return (
                        <div key={key} className={`col mb-2 ${item.paddingCard}`}>
                            <CustomCard classCard={`${item.classCard} card-border-8px`} classContent={"px-8px py-16px"} classes="h-100">
                                <div className={"row"}>
                                    <div className={"d-flex justify-content-start align-items-center p-0"}>
                                        {/* Icon for the card */}
                                        <div>
                                            <item.Icon className={item.iconColor}/>
                                        </div>

                                        <div className={"flex-grow-1 minw-0 ml-2"}>
                                            {/* Title of the card */}
                                            <div className={"flex-grow-1 minw-0"}>
                                                <CustomTooltip
                                                    text={t(item.titleTrans)}
                                                    classes={`${item.fontColor} ibl-font-w-700 fs-14px text-ellipsis-1-row`}
                                                    showIfTruncated={true}
                                                />
                                            </div>

                                            {/* Conditional rendering of name + total or just total */}
                                            {item.name ? (
                                                <div className={"d-flex align-items-center justify-content-between"}>
                                                    <div className={"flex-grow-1 minw-0"}>
                                                        <CustomTooltip
                                                            text={item.name}
                                                            classes={`${item.fontColor} fs-16px mr-2 text-ellipsis-1-row flex-grow-1 minw-0`}
                                                            showIfTruncated={true}
                                                        />
                                                    </div>
                                                    <div className={"flex-grow-1 minw-0 text-end"}>
                                                        <CustomTooltip
                                                            text={item.total}
                                                            classes={`${item.fontColor} ibl-font-w-700 fs-24px text-ellipsis-1-row`}
                                                            showIfTruncated={true}
                                                        />
                                                    </div>
                                                </div>
                                            ) : (
                                                <CustomTooltip
                                                    text={item.total}
                                                    classes={`${item.fontColor} ibl-font-w-700 fs-24px text-ellipsis-1-row`}
                                                    showIfTruncated={true}
                                                />
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </CustomCard>
                        </div>
                    );
                })
            }
        </div>
    );
};

export default TotalMealsCountGridView;
